import { Badge, Box, Group, Text, Tooltip } from "@mantine/core";

import { useT } from "../../i18n/script";

export interface Product {
  vrsta: "ffs" | "dubrivo"; sifra?: string | null; naziv: string; doza?: number | null; jedinica?: string | null; cilj?: string | null;
  karenca_dana?: number | null; napomena?: string | null;
}
export interface Step {
  id?: string; faza: string; bbch_od?: number | null; bbch_do?: number | null; od: string; do: string; vrsta: string; naziv: string;
  opis?: string | null; sredstva: Product[]; uslov?: string | null; obavezno: boolean; voda_l_ha?: number | null;
  ponavljanje_dana?: number | null; ponavljanja?: number | null; navodnjavanje?: { sistem?: string; kolicina_m3_ha?: number } | null;
}
export interface Procedure {
  id: number; code: string; name: string; species_code: string; species_name?: string; cultivar?: string | null; description?: string | null;
  builtin: boolean; owner?: string | null; based_on?: string | null; version: number; active: boolean; steps: Step[] | number;
  editable: boolean; updated_at?: string; updated_by?: string | null; zastita?: number; orchards?: number;
}
export interface Issue { step: string; level: "error" | "warn" | "info"; msg: string }

export const TYPE_COLOR: Record<string, string> = {
  zastita: "red", dubrenje: "green", navodnjavanje: "blue", rezidba: "grape", proredjivanje: "orange", obrada_tla: "lime",
  mreza: "gray", zastita_mraz: "cyan", berba: "yellow", analiza_tla: "teal", sadnja: "indigo", kontrola: "dark", ostalo: "gray",
};
export const TYPE_SHORT: Record<string, string> = {
  zastita: "Zaštita", dubrenje: "Ishrana", navodnjavanje: "Navodnjavanje", rezidba: "Rezidba", proredjivanje: "Proređivanje",
  obrada_tla: "Obrada tla", mreza: "Mreža", zastita_mraz: "Mraz", berba: "Berba", analiza_tla: "Analiza tla", sadnja: "Sadnja",
  kontrola: "Kontrola", vremenski_dogadjaj: "Vrijeme", ostalo: "Ostalo",
};
const MONTHS = ["jan", "feb", "mar", "apr", "maj", "jun", "jul", "avg", "sep", "okt", "nov", "dec"];

export const doy = (mmdd: string) => {
  const [m, d] = mmdd.split("-").map(Number);
  return Math.round((Date.UTC(2025, m - 1, d) - Date.UTC(2025, 0, 1)) / 864e5);
};
export const fmtMd = (mmdd: string) => {
  const [m, d] = mmdd.split("-").map(Number);
  return `${d}. ${MONTHS[m - 1]}`;
};

export function productLine(p: Product) {
  const dose = p.doza == null ? "" : ` ${String(p.doza).replace(".", ",")} ${p.jedinica ?? ""}`;
  return `${p.naziv}${dose}${p.cilj ? ` — ${p.cilj}` : ""}${p.karenca_dana ? ` (karenca ${p.karenca_dana} d)` : ""}`;
}

/** Godišnji prikaz koraka postupka: jedna traka po koraku na kalendaru od januara do decembra. */
export function YearTimeline({ steps, onPick, issues }: { steps: Step[]; onPick?: (s: Step) => void; issues?: Issue[] }) {
  const { t } = useT();
  const today = new Date();
  const todayDoy = doy(`${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`);
  const bad = new Set((issues ?? []).filter((i) => i.level !== "info").map((i) => i.step));
  return (
    <Box style={{ overflowX: "auto" }}>
      <Box style={{ minWidth: 720, position: "relative" }}>
        <Group gap={0} wrap="nowrap" style={{ borderBottom: "1px solid var(--mantine-color-default-border)" }}>
          <Box w={230} />
          {MONTHS.map((m) => <Text key={m} size="xs" c="dimmed" ta="center" style={{ flex: 1 }}>{t(m)}</Text>)}
        </Group>
        {steps.map((s) => {
          const a = doy(s.od) / 365, b = Math.max(doy(s.do) + 1, doy(s.od) + 3) / 365;
          return (
            <Group key={s.id ?? s.naziv} gap={0} wrap="nowrap" py={2} style={{ cursor: onPick ? "pointer" : undefined }}
              onClick={() => onPick?.(s)}>
              <Text size="xs" w={230} pr={8} truncate title={s.naziv} fw={s.obavezno ? 500 : 400} c={bad.has(s.id ?? "") ? "orange" : undefined}>
                {s.naziv}
              </Text>
              <Box style={{ flex: 1, position: "relative", height: 16 }}>
                <Box style={{ position: "absolute", left: `${todayDoy / 3.65}%`, top: -2, bottom: -2, borderLeft: "1px dashed var(--mantine-color-red-5)" }} />
                <Tooltip label={`${TYPE_SHORT[s.vrsta] ?? s.vrsta} · ${fmtMd(s.od)} – ${fmtMd(s.do)}${s.bbch_od != null ? ` · BBCH ${s.bbch_od}${s.bbch_do !== s.bbch_od ? `–${s.bbch_do}` : ""}` : ""}${s.uslov ? ` · ${s.uslov}` : ""}`}
                  multiline maw={360}>
                  <Box style={{
                    position: "absolute", left: `${a * 100}%`, width: `${(b - a) * 100}%`, top: 2, height: 12, borderRadius: 4,
                    background: `var(--mantine-color-${TYPE_COLOR[s.vrsta] ?? "gray"}-${s.obavezno ? 6 : 3})`,
                    border: s.obavezno ? undefined : `1px dashed var(--mantine-color-${TYPE_COLOR[s.vrsta] ?? "gray"}-6)`,
                  }} />
                </Tooltip>
              </Box>
            </Group>
          );
        })}
        <Group gap={6} mt="sm">
          {Array.from(new Set(steps.map((s) => s.vrsta))).map((v) => (
            <Badge key={v} size="xs" color={TYPE_COLOR[v] ?? "gray"} variant="filled">{t(TYPE_SHORT[v] ?? v)}</Badge>
          ))}
          <Text size="xs" c="dimmed">{t("puna traka = obavezno, svijetla = po potrebi (uslov) · crvena linija = danas")}</Text>
        </Group>
      </Box>
    </Box>
  );
}
